import React, { useState, useEffect, useMemo } from "react";
import { useSubmitHandler } from "hooks/useSubmitHandler";
import { useCreateData, useUpdateData } from "hooks/useApiHooks";
import {
  IconCalendar,
  IconClock,
  // IconLocation,
  // IconMoneybag,
  IconPencil,
  IconPrinter,
} from "@tabler/icons-react";
import { useCanvasHandler } from "hooks/useCanvasHandler";
import CreatePage from "./create-page";
import ChangeOrderDate from "./canvas-order/change-order-date";
import ChangeOrderStatus from "./canvas-order/change-order-status";
import { LabelProvider } from "contexts/v1/masterData/LabelContext";
import { TripProvider } from "contexts/v1/masterData/TripContext";
import { ProductProvider } from "contexts/v1/masterData/ProductContext";
import { AgentProvider, useAgent } from "contexts/v1/masterData/AgentContext";
import { useCustomer } from "contexts/v1/masterData/CustomerContext";
import PrintBookingDetails from "./canvas-order/PrintBookingDetails";
import PrintTicket from "./canvas-order/print-ticket";
// import { useGuide } from "contexts/v1/masterData/GuideContext";
import UpdateGuide from "./canvas-order/update-guide";
import { BookingProviderV3, useAppV3 } from "contexts/v3/BookingContextV3";
import { getContrastColor } from "utils/utils";
import { Alert, Badge, Table } from "react-bootstrap";
import UpdatePhotographer from "./canvas-order/update-photographer";
import { PaymentMethodProvider } from "contexts/v1/masterData/PaymentMethodContext";
import { MealProvider } from "contexts/v1/masterData/MealContext";
import PrintBookingActivity from "./canvas-order/PrintBookingActivity";
import PrintBookingExtra from "./canvas-order/PrintBookingExtra";
import PermissionMiddleware from "middleware/PermissionMiddleware";
import PrintCustomBookingActivity from "./canvas-order/PrintCustomBookingActivity";
import Swal from "sweetalert2";
import { UserProvider } from "contexts/v1/masterData/UserContext";
import { useGuide } from "../context/GuideContext";
import { sendBookingUpdatedEvent } from "services/websocket/websocketUtils";

const CardClickPage = ({
  initialData,
  onSuccess,
  onError,
  refetchTrip,
  userRoles,
  updateFilter,
}) => {
  const API_URL = "/booking"; // API URL sesuai dengan endpoint booking Anda
  const { guides } = useGuide();
  // console.log("guides card click", guides.data);

  // console.log("label", labels);
  // console.log("trips", trips);
  // console.log("CardClickPage initialData", initialData);
  const { refetch, sessions } = useAppV3();
  const { customers } = useCustomer();
  const { agents } = useAgent();

  // Transform initialData to fix amount values in payment_splits
  const transformedInitialData = useMemo(() => {
    if (!initialData) return initialData;

    const transformedPaymentSplits = initialData.payment_splits
      ? initialData.payment_splits.map((split) => {
          const amount = parseInt(split.amount || 0);
          // If amount has 8 or more digits, divide by 100
          const shouldDivide = amount >= 10000000;
          return {
            ...split,
            amount: shouldDivide ? amount / 100 : amount,
          };
        })
      : initialData.payment_splits;

    return {
      ...initialData,
      payment_splits: transformedPaymentSplits,
    };
  }, [initialData]);

  // Use transformed data for display
  const displayData = transformedInitialData || initialData;

  // console.log("CardClickPage refetch", refetch);
  const [formData, setFormData] = useState({
    // productSelect: "",
    selectedProducts: [],
    price: "",
    quantity: 1,
  });

  // Helper function to get agent data by id
  const getAgentById = (agentId) => {
    return agents.data.find((a) => a.id === agentId) || null; // Kembalikan seluruh data agent
  };

  // Dapatkan data agent berdasarkan booking.agent_id
  const agent = getAgentById(initialData.agent_id);

  const getGuideById = (guideId) => {
    return guides.data.find((g) => g.id === guideId) || null;
  };

  const guide = getGuideById(initialData.guide_id);

  const isUpdateMode = !!initialData;
  const { handleSuccess, handleError, showCanvasHandler, handleSuccessToast } =
    useCanvasHandler();

  const { createData: createBooking, loading: creatingBooking } = useCreateData(
    API_URL,
    "bookings"
  );
  const { updateData: updateBooking, loading: updatingBooking } = useUpdateData(
    `${API_URL}/${initialData?.id}`,
    "bookings"
  );

  const { handleSubmit, isSubmitting, formError } = useSubmitHandler(
    isUpdateMode ? updateBooking : createBooking,
    async (responseData) => {
      onSuccess?.(responseData);

      if (isUpdateMode) {
        sendBookingUpdatedEvent(responseData).catch((err) =>
          console.warn(
            "[BOOKING_V4_WEBSOCKET] Failed to send booking update event:",
            err
          )
        );
      }
    },
    onError
  );

  const handleBeforeSubmit = (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
    };

    handleSubmit(payload, e);
  };

  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
      });
    }
  }, [initialData]);

  //role can view
  // const userRoles = user?.roles?.map((role) => role.name) || [];

  const canChangeDate = userRoles.some((role) =>
    ["superadmin", "admin", "reservation"].includes(role)
  );

  // bisa print jika status booking adalah confirmed, played, finished
  const canPrint = ["confirmed", "played", "finished"].includes(
    initialData.status
  );

  // Cek apakah status booking butuh permission khusus untuk update
  const statusNeedsPermission = ["played", "finished"].includes(
    initialData.status
  );

  // Fungsi untuk render Update Booking jika diizinkan
  const requirePermission = ["played", "finished"].includes(initialData.status);

  const actions = [
    // { icon: <IconCalendar size={24} />, label: "Change Date" },
    ...(canChangeDate
      ? [{ icon: <IconCalendar size={24} />, label: "Change Date" }]
      : []),
    // { icon: <IconPencil size={24} />, label: "Update Booking" },
    // Render update booking sesuai kondisi status dan permission
    ...(statusNeedsPermission
      ? [] // jika butuh permission, kita render pakai PermissionMiddleware nanti
      : [{ icon: <IconPencil size={24} />, label: "Update Booking" }]),
    ...(canPrint
      ? [
          {
            icon: <IconPrinter size={24} />,
            label: "Print Guest Invoice",
            type: "customer",
          },
          // {
          //   icon: <IconPrinter size={24} />,
          //   label: "Print Invoice Activity Only",
          //   type: "activity",
          // },
          // {
          //   icon: <IconPrinter size={24} />,
          //   label: "Print Invoice Extra Only",
          //   type: "extra",
          // },
          {
            icon: <IconPrinter size={24} />,
            label: "Print Ticket & Meal",
            type: "ticket",
          },
          {
            icon: <IconPrinter size={24} />,
            label: "Custom Print Activity",
            type: "custom",
          },
        ]
      : []),
  ];

  const handleAction = (e, action) => {
    e.preventDefault();
    // hideCanvas();

    if (action.label === "Detail Booking") {
      showCanvasHandler(
        "Detail Booking",
        // <DetailBooking order={order} />
        <CreatePage onSuccess={handleSuccess} onError={handleError} />
      );
    } else if (action.label === "Change Date") {
      showCanvasHandler(
        "Change Date",
        <ChangeOrderDate
          initialData={initialData}
          onSuccess={() => {
            handleSuccess(true, "Change Date successfully");
            // updateFilter("reservation_date", initialData.reservation_date);
            refetch(); // ✅ Refetch data booking setelah mengganti tanggal
          }}
        />
      );
    } else if (action.label === "Update Booking") {
      showCanvasHandler(
        "Update Booking - " + initialData.code,
        <ProductProvider>
          <AgentProvider>
            <LabelProvider>
              <TripProvider>
                <BookingProviderV3>
                  <PaymentMethodProvider>
                    <UserProvider>
                      <CreatePage
                        initialData={initialData}
                        refetch={refetch}
                        onSuccess={() => {
                          handleSuccess(
                            true,
                            "Reservation Update successfully"
                          );
                          refetch();
                          refetchTrip();
                        }}
                        onError={handleError}
                        sessions={sessions}
                        customers={customers}
                        userRoles={userRoles}
                      />
                    </UserProvider>
                  </PaymentMethodProvider>
                </BookingProviderV3>
              </TripProvider>
            </LabelProvider>
          </AgentProvider>
        </ProductProvider>,
        "fullscreen-canvas"
      );
    } else if (action.label === "Print Guest Invoice") {
      showCanvasHandler(
        "Print Guest Invoice",
        <PrintBookingDetails
          booking={initialData}
          agent={agent}
          onSuccess={() => {
            handleSuccessToast(true, "Reservation Print successfully");
            refetch();
          }}
          onError={handleError}
          refetch={refetch}
        />
      );
    } else if (action.label === "Print Invoice Activity Only") {
      const productActivitiesV1 = initialData.activities?.filter(
        (item) => item.type === "product"
      );
      const productActivities = [
        ...(initialData.activities?.filter(
          (item) => item.type === "product" || item.activity === "Rafting"
        ) || []),
      ];

      showCanvasHandler(
        "Print Invoice Activity Only",
        <PrintBookingActivity
          booking={{ ...initialData, activities: productActivities }}
          agent={agent}
          onSuccess={() => {
            handleSuccessToast(true, "Reservation Print successfully");
            refetch();
          }}
          onError={handleError}
          refetch={refetch}
        />
      );
    } else if (action.label === "Print Invoice Extra Only") {
      const extraActivitiesV1 = initialData.activities?.filter(
        (item) => item.type === "extra"
      );

      const extraActivities = [
        ...(initialData.activities?.filter(
          (item) => item.type === "extra" && item.activity !== "Rafting"
        ) || []),
      ];

      showCanvasHandler(
        "Print Invoice Extra Only",
        <PrintBookingExtra
          booking={{ ...initialData, activities: extraActivities }}
          agent={agent}
          onSuccess={() => {
            handleSuccessToast(true, "Reservation Print successfully");
            refetch();
          }}
          onError={handleError}
          refetch={refetch}
        />
      );
    } else if (action.label === "Print Ticket & Meal") {
      showCanvasHandler(
        "Print Ticket & Meal",
        <ProductProvider>
          <AgentProvider>
            <LabelProvider>
              <MealProvider>
                <PrintTicket
                  initialData={initialData}
                  refetch={refetch}
                  // onSuccess={handleSuccess}
                  onSuccess={() => {
                    handleSuccess(true, "Reservation Update successfully");
                    refetch();
                    // setTimeout(() => {
                    //   window.location.reload();
                    // }, 1000); // Delay 1 second before reloading
                  }}
                  onError={handleError}
                  sessions={sessions}
                  customers={customers}
                  guides={guides}
                />
              </MealProvider>
            </LabelProvider>
          </AgentProvider>
        </ProductProvider>
      );
    } else if (action.label === "Custom Print Activity") {
      showCanvasHandler(
        "Custom Print Activity",
        <ProductProvider>
          <AgentProvider>
            <LabelProvider>
              {/* <PrintBookingActivity
                booking={initialData}
                agent={agent}
                onSuccess={() => {
                  handleSuccessToast(true, "Reservation Print successfully");
                  refetch();
                }}
                onError={handleError}
                refetch={refetch}
              /> */}
              <PrintCustomBookingActivity
                booking={{ ...initialData }}
                agent={agent}
                refetch={refetch}
                // onSuccess={...}
                // onError={...}
                isCustom={true} // penting!
              />
            </LabelProvider>
          </AgentProvider>
        </ProductProvider>
      );
    } else {
      return null;
    }
  };

  const handleUpdate = (e, data) => {
    e.preventDefault();
    showCanvasHandler(
      "Update Booking",
      <ProductProvider>
        <AgentProvider>
          <LabelProvider>
            <CreatePage
              initialData={data}
              // onSuccess={handleSuccess}
              onSuccess={() => {
                handleSuccess(true, "Reservation Update successfully");
                refetch();
              }}
              onError={handleError}
              sessions={sessions}
              customers={customers}
            />
          </LabelProvider>
        </AgentProvider>
      </ProductProvider>
    );
  };

  // Fungsi utilitas
  const formatPrice = (price) => {
    return new Intl.NumberFormat("id-ID").format(price);
  };

  const transportType = initialData.guest_transport;

  // Lookup table untuk label & warna transportasi
  const transportDetails = {
    PU: {
      label: "Pick Up & Drop",
      badgeColor: "success",
      alertColor: "success",
    },
    D: { label: "Drop Only", badgeColor: "primary", alertColor: "primary" },
    P: { label: "Pick Only", badgeColor: "warning", alertColor: "warning" },
    OT: { label: "Own Transport", badgeColor: "danger", alertColor: "danger" },
  };

  // Default jika tidak sesuai dengan opsi
  const transport = transportDetails[transportType] || {
    label: "Unknown",
    badgeColor: "secondary",
    alertColor: "dark",
  };

  return (
    <>
      {/* card untuk menampilkan sedikit detail dari booking, seperti
      produk/akitfitas yang dipilih, jumlah tamu  */}
      <div className="card card-md mb-3">
        <div className="card-body">
          {/* jika di dalam inital data ada data dari revisions tampilkan desain alert bahwa reservasi masi dalam tahap revisi */}
          {initialData.revisions?.length > 0 && (
            <Alert variant="warning" className="mb-4">
              <div className="d-flex align-items-center">
                <i
                  className="bi bi-exclamation-triangle"
                  style={{ fontSize: "1.5rem", marginRight: "10px" }}
                ></i>
                <span>
                  <strong>Revision Notice:</strong> This reservation is
                  currently under revision. <br />
                </span>
              </div>
            </Alert>
          )}
          <div className="row d-flex justify-content-between">
            <div className="col-md-6">
              <div className="mb-3">
                <h4 className="card-title mb-2">
                  Booking Code: {initialData.code}
                  {/* <pre>{JSON.stringify(initialData, null, 2)}</pre> */}
                </h4>

                <Table
                  bordered
                  className="mb-2"
                  size="sm"
                  style={{ fontSize: "1rem" }}
                >
                  <tbody>
                    <tr>
                      <td className="text-muted fw-bold">Date</td>
                      <td>{initialData.reservation_date || "-"}</td>
                    </tr>
                    <tr>
                      <td className="text-muted fw-bold">Session</td>
                      <td>
                        {initialData.trip?.name || "-"} |{" "}
                        {initialData.trip?.start_time || "??:??"} -{" "}
                        {initialData.trip?.end_time || "??:??"}
                      </td>
                    </tr>
                    {/* ACTIVITY */}
                    <tr>
                      <td className="text-muted fw-bold">Activity</td>
                      <td>
                        {initialData.activities?.length > 0 ? (
                          initialData.activities.map((activity) => (
                            <div key={activity.id} className="mb-1">
                              <span className="">
                                ({activity.qty}x) : {activity.activity} : Rp.
                                {formatPrice(activity.price)}
                              </span>
                            </div>
                          ))
                        ) : (
                          <span className="text-muted">No Activity</span>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted fw-bold">Customer</td>
                      <td>{initialData.customer?.name || "-"}</td>
                    </tr>
                    <tr>
                      <td className="text-muted fw-bold">Hotel</td>
                      <td>
                        {initialData.customer?.nationality ||
                          initialData.guest_nationality ||
                          "-"}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted fw-bold">Phone</td>
                      <td>
                        {initialData.customer?.phone ||
                          initialData.guest_phone ||
                          "-"}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted fw-bold">Email</td>
                      <td>
                        {initialData.customer?.email ||
                          initialData.guest_email ||
                          "-"}
                      </td>
                    </tr>

                    <tr>
                      <td className="text-muted fw-bold">Transport</td>
                      <td className="d-flex align-items-center gap-2">
                        <Badge
                          bg={transport.badgeColor}
                          className="text-uppercase text-white"
                        >
                          {transport.label}
                        </Badge>
                        {["PU", "D", "P"].includes(transportType) && initialData.guest_pu_time && (
                          <small className="text-muted fw-bold d-flex align-items-center">
                            <IconClock size={14} className="me-1" />
                            {initialData.guest_pu_time.slice(0, 5)}
                          </small>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted fw-bold">PAX</td>
                      <td>{initialData.guest_pax || 0}</td>
                    </tr>
                    {/* Guest Mark */}
                    <tr>
                      <td className="text-muted fw-bold">Guest Mark</td>
                      <td>{initialData.guest_mark || "-"}</td>
                    </tr>

                    <tr>
                      <td className="text-muted fw-bold">Agent</td>
                      <td>{initialData.agent?.name || "-"}</td>
                    </tr>
                    {initialData.labels?.length > 0 && (
                      <tr>
                        <td className="text-muted fw-bold">Label</td>
                        <td className="d-flex flex-wrap gap-2">
                          {initialData.labels.map((label) => (
                            <span
                              key={label.id}
                              className="badge text-uppercase"
                              title="Status Booking"
                              style={{
                                backgroundColor: label.color || "#ccc", // Warna dari API
                                color: getContrastColor(label.color), // Pastikan teks tetap terbaca
                              }}
                            >
                              {label.name}
                            </span>
                          ))}
                        </td>
                      </tr>
                    )}
                    <tr>
                      <td className="text-muted fw-bold">Driver</td>
                      <td>{initialData.driver || "-"}</td>
                    </tr>
                    {/* payment methode */}
                    {displayData.payment_splits &&
                    displayData.payment_splits.length > 0 ? (
                      <>
                        <tr>
                          <td className="text-muted fw-bold">Payment Type</td>
                          <td>
                            <Badge bg="info">Split Payment</Badge>
                          </td>
                        </tr>
                        <tr>
                          <td className="text-muted fw-bold">Payment Splits</td>
                          <td>
                            <div className="d-flex flex-column gap-2">
                              {displayData.payment_splits.map(
                                (split, index) => (
                                  <div
                                    key={index}
                                    className="border rounded p-2 bg-light"
                                  >
                                    <div className="d-flex justify-content-between align-items-center">
                                      <small className="fw-bold">
                                        Payment #{index + 1}:{" "}
                                        {split.payment_method?.name ||
                                          "Unknown"}
                                      </small>
                                      <small className="text-success fw-bold">
                                        Total: Rp{" "}
                                        {(
                                          parseInt(split.amount || 0) +
                                          (parseInt(split.amount || 0) *
                                            split.tax) /
                                            100
                                        ).toLocaleString("id-ID")}
                                      </small>
                                    </div>
                                    <div className="mt-1">
                                      <small className="text-muted">
                                        Base: Rp{" "}
                                        {parseInt(
                                          split.amount || 0
                                        ).toLocaleString("id-ID")}
                                        {split.tax > 0 && (
                                          <>
                                            <br />
                                            Tax ({split.tax}%): Rp{" "}
                                            {(
                                              (parseInt(split.amount || 0) *
                                                split.tax) /
                                              100
                                            ).toLocaleString("id-ID")}
                                          </>
                                        )}
                                      </small>
                                    </div>
                                    {split.note && (
                                      <div className="mt-1">
                                        <small className="text-muted">
                                          Note: {split.note}
                                        </small>
                                      </div>
                                    )}
                                  </div>
                                )
                              )}
                            </div>
                          </td>
                        </tr>
                      </>
                    ) : (
                      <tr>
                        <td className="text-muted fw-bold">Payment Method</td>
                        <td>{initialData.payment_method?.name || "-"}</td>
                      </tr>
                    )}
                    {/* Payment Note - only show for single payment */}
                    {(!displayData.payment_splits ||
                      displayData.payment_splits.length === 0) && (
                      <tr>
                        <td className="text-muted fw-bold">Payment Note</td>
                        <td>{initialData.payment_note || "-"}</td>
                      </tr>
                    )}
                    {/* 4 Last Number: */}
                    <tr>
                      <td className="text-muted fw-bold">4 Last Number</td>
                      <td>{initialData.last_number || "-"}</td>
                    </tr>
                    {/* Note */}
                    <tr>
                      <td className="text-muted fw-bold">Note</td>
                      <td>{initialData.note || "-"}</td>
                    </tr>
                    {/* Host By */}
                    <tr>
                      <td className="text-muted fw-bold">Host By</td>
                      <td>{initialData.host?.name || "-"}</td>
                    </tr>
                  </tbody>
                  {/* print json initialData */}
                </Table>
                {/* <pre>{JSON.stringify(initialData, null, 2)}</pre> */}
              </div>
              {/* {initialData.labels?.length > 0 && (
                <div className="text-muted mb-3">
                  <strong>Label:</strong>
                  <div className="d-flex flex-wrap gap-2 mt-1">
                    {initialData.labels.map((label) => (
                      <span
                        key={label.id}
                        className="badge text-uppercase"
                        title="Status Booking"
                        style={{
                          backgroundColor: label.color || "#ccc", // Warna dari API
                          color: getContrastColor(label.color), // Pastikan teks tetap terbaca
                        }}
                      >
                        {label.name}
                      </span>
                    ))}
                  </div>
                </div>
              )} */}

              {/* Transportasi */}
              {/* <p className="text-muted">
                <strong>Transportasi:</strong>{" "}
                <Badge
                  bg={transport.badgeColor}
                  className="text-uppercase text-white"
                >
                  {transport.label}
                </Badge>
              </p> */}
              {/* Transport Time moved to table */}
            </div>
            <div className="col-md-6">
              {/* buat tombol untuk langsung change status atau option yg langsung bisa mengubah status */}
              <div className="col-md-12 mb-3 ">
                {requirePermission ? (
                  <PermissionMiddleware allowedPermissions={["update_status"]}>
                    <ChangeOrderStatus
                      initialData={initialData}
                      // onSuccess={onSuccess}
                      onSuccess={() => {
                        handleSuccessToast(
                          true,
                          "Reservation Update successfully"
                        );
                        // refetch();
                        onSuccess?.(); // Call parent onSuccess to trigger main page refetch
                        // setTimeout(() => {
                        //   window.location.reload();
                        // }, 1000); // Delay 1 second before reloading
                      }}
                      // refetch={refetch}
                    />
                  </PermissionMiddleware>
                ) : (
                  <ChangeOrderStatus
                    initialData={initialData}
                    // onSuccess={onSuccess}
                    onSuccess={() => {
                      handleSuccessToast(
                        true,
                        "Reservation Update successfully"
                      );
                      // refetch();
                        onSuccess?.(); // Call parent onSuccess to trigger main page refetch
                      // setTimeout(() => {
                      //   window.location.reload();
                      // }, 1000); // Delay 1 second before reloading
                    }}
                    // refetch={refetch}
                  />
                )}
              </div>
              {/* select option untuk guide trip */}
              <div className="col-md-12 mb-3 ">
                {/* NB: Pilih Guide & Photographer masih dalam proses perbaikan */}
                <UpdateGuide
                  initialData={initialData}
                  onSuccess={() => {
                    // handleSuccessToast(true, "Guide updating");
                    Swal.fire("Success", "Guide has been updated", "success");
                    refetch();
                  }}
                  onError={handleError}
                  guides={guides} // Pass guides data to UpdateGuide
                />
              </div>
              {/* update photographer */}
              <div className="col-md-12 mb-3 ">
                <UpdatePhotographer
                  initialData={initialData}
                  onSuccess={() => {
                    // handleSuccessToast(true, "Photographer updating");
                    Swal.fire(
                      "Success",
                      "Photographer has been updated",
                      "success"
                    );
                    refetch();
                  }}
                  onError={handleError}
                />
              </div>
              <div className="col-md-12">
                {initialData.track === "A" ? (
                  <div className="col-md-12 text-center">
                    <div className="alert alert-info" role="alert">
                      <h4 className="alert-heading mb-0">Trip Option A</h4>
                      <p>
                        <span style={{ fontSize: "60px", fontWeight: "bold" }}>
                          PREMIUM
                        </span>
                      </p>
                    </div>
                  </div>
                ) : initialData.track === "B" ? (
                  <div className="col-md-12 text-center">
                    <div className="alert alert-google" role="alert">
                      <h4 className="alert-heading mb-0">Trip Option B</h4>
                      <p>
                        <span style={{ fontSize: "60px", fontWeight: "bold" }}>
                          REGULAR
                        </span>
                      </p>
                    </div>
                  </div>
                ) : null}
              </div>
              {/* <p className="text-muted">Total: Rp. 100.000</p> */}
            </div>
          </div>
        </div>
      </div>
      <form
        onSubmit={handleBeforeSubmit}
        className="d-flex flex-column"
        style={{ height: "100%" }}
      >
        <ul>
          {requirePermission && (
            <PermissionMiddleware allowedPermissions={["update_data_booking"]}>
              <li
                className="mb-5 d-flex align-items-center cursor-pointer"
                onClick={(e) =>
                  handleAction(e, {
                    icon: <IconPencil size={24} />,
                    label: "Update Booking",
                  })
                }
              >
                <IconPencil size={24} />
                <span className="ms-2">Update Booking</span>
              </li>
            </PermissionMiddleware>
          )}
          {actions.map((action, index) => (
            <li
              key={index}
              className={`mb-5 d-flex align-items-center cursor-pointer ${
                action.isDanger ? "text-danger" : ""
              }`}
              onClick={(e) => handleAction(e, action)}
            >
              {action.icon}
              <span className="ms-2">{action.label}</span>
            </li>
          ))}
        </ul>
      </form>
      {/* print json initial data */}
      {/* <pre>{JSON.stringify(initialData, null, 2)}</pre> */}
    </>
  );
};

export default CardClickPage;
